import type { AgentTokenListItem } from "../../../../services/agent-token-service";
import type { KuikaBadgeColor } from "../../../ui-kit/kuika-badge/kuika-badge";

// Pure helpers for the Agent Tokens tab — kept out of the component so the
// status rules are unit-testable without rendering.

export type AgentTokenStatus = "active" | "expiring" | "expired" | "revoked";

export interface AgentTokenStatusView {
  status: AgentTokenStatus;
  label: string;
  color: KuikaBadgeColor;
}

const DAY_MS: number = 24 * 60 * 60 * 1000;
/** Inside this window an active token is flagged so its owner can rotate it before clients break. */
export const EXPIRING_SOON_MS: number = 14 * DAY_MS;
export const MAX_LABEL_LENGTH: number = 80;

const DATE_FORMATTER: Intl.DateTimeFormat = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric"
});

/** Server-side cap (mcp-chat-server `MAX_ACTIVE_TOKENS_PER_USER`) — shown so the limit is never a surprise. */
export const MAX_ACTIVE_TOKENS: number = 20;

/** Served from the designer's own static files; built by `pnpm plugin:zip` before every build. */
export const PLUGIN_DOWNLOAD_URL: string = "/downloads/kuika-claude-plugin.zip";
export const PLUGIN_DOWNLOAD_FILE_NAME: string = "kuika-claude-plugin.zip";

export interface AgentTokenSummary {
  activeCount: number;
  /** Earliest expiry among tokens that still work; undefined when there are none. */
  nextExpiryAt?: number;
}

export function summarizeAgentTokens(tokens: AgentTokenListItem[], now: number): AgentTokenSummary {
  const usable: AgentTokenListItem[] = tokens.filter((token: AgentTokenListItem): boolean =>
    isAgentTokenUsable(token, now)
  );
  if (usable.length === 0) return { activeCount: 0 };
  const nextExpiryAt: number = Math.min(...usable.map((token: AgentTokenListItem): number => token.expiresAt));
  return { activeCount: usable.length, nextExpiryAt };
}

/** Revoked wins over expired: it is the more specific (and deliberate) reason a token is dead. */
export function resolveAgentTokenStatus(token: AgentTokenListItem, now: number): AgentTokenStatusView {
  if (token.revokedAt !== undefined && token.revokedAt !== null) {
    return { status: "revoked", label: "Revoked", color: "red" };
  }
  if (token.expiresAt <= now) {
    return { status: "expired", label: "Expired", color: "grey" };
  }
  if (token.expiresAt - now <= EXPIRING_SOON_MS) {
    const daysLeft: number = Math.max(1, Math.ceil((token.expiresAt - now) / DAY_MS));
    return { status: "expiring", label: `Expires in ${daysLeft}d`, color: "yellow" };
  }
  return { status: "active", label: "Active", color: "green" };
}

export function isAgentTokenUsable(token: AgentTokenListItem, now: number): boolean {
  const status: AgentTokenStatus = resolveAgentTokenStatus(token, now).status;
  return status === "active" || status === "expiring";
}

export function formatAgentTokenDate(epochMs: number | undefined): string {
  if (epochMs === undefined || epochMs === null || !Number.isFinite(epochMs)) return "-";
  return DATE_FORMATTER.format(new Date(epochMs));
}

/** Usable tokens first (newest first), then dead ones (newest first) — the list a user acts on stays on top. */
export function sortAgentTokens(tokens: AgentTokenListItem[], now: number): AgentTokenListItem[] {
  return [...tokens].sort((a: AgentTokenListItem, b: AgentTokenListItem): number => {
    const usableDelta: number = Number(isAgentTokenUsable(b, now)) - Number(isAgentTokenUsable(a, now));
    if (usableDelta !== 0) return usableDelta;
    return b.createdAt - a.createdAt;
  });
}

/** Must match the server name the Claude Code plugin's destructive-op hook matches on. */
export const MCP_SERVER_NAME: string = "kuika-unified";

/** `https://host[/base]` → `https://host[/base]/mcp`, tolerant of trailing slashes. */
export function resolveMcpUrl(aiChatUrl: string): string {
  return `${aiChatUrl.trim().replace(/\/+$/, "")}/mcp`;
}

/**
 * The one command that connects Claude Code: paste, run, done — no environment
 * variables and no config file to edit. `--scope user` makes it available in
 * every project. Only double quotes are used, so the same line works in bash,
 * zsh, PowerShell and cmd. An agent token is base64url plus a dot, so it needs
 * no escaping inside them.
 */
/**
 * `claude mcp add` refuses to overwrite ("already exists in user config") and
 * has no force flag, so replacing a token — rotation, a revoked token, a second
 * try — needs the old entry removed first. Kept as its OWN command rather than
 * chained in front of the add: on a first install it would fail with a
 * confusing "not found", and no separator is portable across bash,
 * PowerShell and cmd.
 */
export function buildRemoveCommand(): string {
  return `claude mcp remove ${MCP_SERVER_NAME} --scope user`;
}

export function buildSetupCommand(aiChatUrl: string, agentToken: string): string {
  return [
    "claude mcp add --transport http --scope user",
    MCP_SERVER_NAME,
    resolveMcpUrl(aiChatUrl),
    `--header "Authorization: Bearer ${agentToken}"`
  ].join(" ");
}
